import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Mail, Lock, User, Phone, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';

export default function RegisterPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  // V5.45 : Séparation first_name/last_name + phone obligatoire
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!acceptedTerms) {
      toast.error('Vous devez accepter les conditions d\'utilisation');
      return;
    }
    
    // V5.45 : Validation des champs obligatoires
    if (!formData.first_name.trim()) {
      toast.error('Le prénom est obligatoire');
      return;
    }
    
    if (!formData.last_name.trim()) {
      toast.error('Le nom est obligatoire');
      return;
    }
    
    if (!formData.phone.trim()) {
      toast.error('Le numéro de téléphone est obligatoire');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (formData.password.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    
    setLoading(true);
    
    try {
      // V5.45 : Envoyer first_name/last_name séparés
      const response = await axios.post(`${API}/auth/register`, {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email,
        phone: formData.phone,
        password: formData.password
      });
      
      if (response.data.status === 'success') {
        // V1.2 PAYWALL : après création du compte, on envoie directement vers l'onglet
        // Paiement du Dashboard (seule page légitime de choix/achat de pack avec CGV,
        // dans la bonne DA cyber sombre). Le tunnel reste verrouillé tant qu'un pack
        // n'est pas acheté (garde côté backend + /missions/entitlements).
        toast.success('Compte créé — choisissez votre pack pour continuer');
        localStorage.setItem('token', response.data.token);
        navigate('/dashboard?tab=payment', { state: { user: response.data.user } });
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la création du compte';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  
  return (
    <div className="min-h-screen bg-void flex items-center justify-center px-4 py-12">
      <div className="absolute inset-0 grid-bg opacity-20" />
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative z-10"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="font-orbitron text-2xl font-bold text-cyan-400 glitch-subtle" data-testid="logo">
            L'EFFACEUR
          </Link>
          <p className="text-gray-500 font-mono text-sm mt-2">// CRÉATION_DE_COMPTE</p>
        </div>
        
        {/* Register Card */}
        <div className="glass-card p-8 border border-gray-800" data-testid="register-form">
          <h1 className="font-orbitron text-xl font-bold text-white mb-6 text-center">
            INSCRIPTION
          </h1>
          
          {/* Google Login */}
          <Button 
            onClick={handleGoogleLogin}
            className="w-full bg-transparent border border-gray-700 text-gray-300 hover:border-cyan-400 hover:text-cyan-400 mb-6 py-6"
            data-testid="google-register-btn"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            S'inscrire avec Google
          </Button>
          
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-800"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="px-4 bg-void text-gray-500 font-mono">OU</span>
            </div>
          </div>
          
          {/* Registration Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* V5.45 : Prénom et Nom séparés */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="first_name" className="text-gray-400 font-mono text-xs uppercase tracking-wider">
                  Prénom *
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    id="first_name"
                    name="first_name"
                    type="text"
                    value={formData.first_name}
                    onChange={handleChange}
                    placeholder="Jean"
                    className="pl-10 bg-gray-900/50 border-gray-800 text-white placeholder:text-gray-600 focus:border-cyan-400"
                    required
                    data-testid="first-name-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="last_name" className="text-gray-400 font-mono text-xs uppercase tracking-wider">
                  Nom *
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    id="last_name"
                    name="last_name"
                    type="text"
                    value={formData.last_name}
                    onChange={handleChange}
                    placeholder="Dupont"
                    className="pl-10 bg-gray-900/50 border-gray-800 text-white placeholder:text-gray-600 focus:border-cyan-400"
                    required
                    data-testid="last-name-input"
                  />
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-400 font-mono text-xs uppercase tracking-wider">
                Email *
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="votre@email.fr"
                  className="pl-10 bg-gray-900/50 border-gray-800 text-white placeholder:text-gray-600 focus:border-cyan-400"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-gray-400 font-mono text-xs uppercase tracking-wider">
                Téléphone *
              </Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="06 12 34 56 78"
                  className="pl-10 bg-gray-900/50 border-gray-800 text-white placeholder:text-gray-600 focus:border-cyan-400"
                  required
                  data-testid="phone-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-400 font-mono text-xs uppercase tracking-wider">
                Mot de passe *
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="pl-10 bg-gray-900/50 border-gray-800 text-white placeholder:text-gray-600 focus:border-cyan-400"
                  required
                  data-testid="password-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-gray-400 font-mono text-xs uppercase tracking-wider">
                Confirmer le mot de passe *
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="pl-10 bg-gray-900/50 border-gray-800 text-white placeholder:text-gray-600 focus:border-cyan-400"
                  required
                  data-testid="confirm-password-input"
                />
              </div>
            </div>
            
            {/* Terms Checkbox */}
            <div className="flex items-start gap-3 pt-2">
              <Checkbox 
                id="terms" 
                checked={acceptedTerms}
                onCheckedChange={setAcceptedTerms}
                className="mt-1 border-cyan-400 data-[state=checked]:bg-cyan-400 data-[state=checked]:text-black"
                data-testid="terms-checkbox"
              />
              <label htmlFor="terms" className="text-gray-400 text-xs leading-relaxed cursor-pointer">
                <span className="font-mono text-cyan-400">[ ] SIGN_KEY_INITIATED...</span><br />
                J'ai pris connaissance du{' '}
                <Link to="/confidentialite" className="text-cyan-400 hover:underline">Protocole Zéro Trace</Link>
                {' '}et j'accepte le traitement de mes données selon la{' '}
                <Link to="/confidentialite" className="text-cyan-400 hover:underline">Charte de Confidentialité</Link>.
              </label>
            </div>
            
            <Button 
              type="submit" 
              disabled={loading || !acceptedTerms}
              className="w-full bg-cyan-400 text-black hover:bg-cyan-300 font-orbitron uppercase tracking-wider py-6 mt-4 disabled:opacity-50"
              data-testid="register-submit-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Créer Mon Compte
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </form>
          
          <p className="text-center text-gray-500 text-sm mt-6">
            Déjà un compte ?{' '}
            <Link to="/login" className="text-cyan-400 hover:underline" data-testid="login-link">
              Se connecter
            </Link>
          </p>
        </div>
        
        {/* Back to Home */}
        <div className="text-center mt-6">
          <Link to="/" className="text-gray-500 hover:text-cyan-400 text-sm font-mono transition-colors">
            ← Retour à l'accueil
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
